import cn from "classnames";
import { Box } from "frontend/ui-components/design-system/box/box";
import { useAtom } from "jotai";
import { useBoardValue } from "frontend/state/board-state";
import { memo, useMemo, useRef, useState } from "react";
import { useCordData, useSearchCordThreads } from "frontend/hooks/use-cord";
import styles from "./comments-panel.module.css";
import { CommentsActiveIcon } from "../../icons";
import { CordThread } from "frontend/canvas-designer-new/cord/cord-thread/cord-thread";
import { TextInput } from "frontend/ui-components/design-system/text-input";
import { SearchIcon } from "frontend/icons/search";
import { SidepanelType, sidePanelType } from "state-atoms";
import { CloseIcon } from "frontend/icons/close";

export const CommentsPanel = memo(() => {
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);
  const [{ board }, _] = useBoardValue();
  const cordData = useCordData(board?.documentId ?? "");
  const floaterRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");

  const unresolvedThreads = useMemo(() => {
    return cordData.threads.filter((thread) => !thread.resolved);
  }, [cordData.threads]);

  const filteredThreads = useSearchCordThreads(unresolvedThreads, search, board?.documentId);

  if (openedSidePanel !== SidepanelType.comments || unresolvedThreads.length === 0) {
    return null;
  }

  return (
    <Box variant="modal" className={cn(styles.commentsContent)} ref={floaterRef}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <CommentsActiveIcon />
          <div className={styles.title}>Comments</div>
        </div>
        <div className={styles.closeIcon}>
          <CloseIcon onClick={() => setOpenedSidePanel(SidepanelType.none)} color="#848199" />
        </div>
      </div>
      {unresolvedThreads.length > 0 && (
        <div className={styles.searchContainer}>
          <TextInput
            leftIcon={<SearchIcon scale={1.2} color="#7F869B" />}
            variant="small"
            placeholder="Search comments"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      <div className={styles.divider} />
      {filteredThreads.length === 0 && (
        <div className={styles.noComments}>
          <div className={styles.title}>No comments found</div>
        </div>
      )}
      {filteredThreads.length > 0 && (
        <div className={styles.threads}>
          {filteredThreads.map((thread) => (
            <CordThread key={thread.id} thread={thread} users={cordData.users} />
          ))}
        </div>
      )}
    </Box>
  );
});

CommentsPanel.displayName = "CommentsPanel";
